/**
 * The `validation` FeatureModule — biological validation (SIL/LIS clinical-decision layer).
 *
 * Screens:
 *   /validation            WorklistScreen   — the sign-off worklist (VAL-1/D-15) + D-24 corrections tab
 *   /validation/escalades  EscaladeScreen   — the D-29 critical-value escalation (read-back ack,
 *                                             ladder attempts, break-glass)
 *
 * Gating (server capability keys, server/src/modules/validation/routes.js):
 *   - module reach:  validation.resultat.read       (the worklist GET; the baseline to enter)
 *   - sign-off:      validation.resultat.signoff     (gated per-action in the worklist)
 *   - escalation:    validation.valeurcritique.acknowledge (ack/tentative/break-glass; gates the
 *                    escalade nav item + every drawer action)
 *
 * Adding this module is one line in src/app/registry.ts (`import validationModule from "@/features/validation"`)
 * — the router/shell/landing compose from the registry. This feature edits nothing outside its folder.
 */
import type { FeatureModule } from "@/app";
import "./validation.css";
import { registerValidationMessages } from "./messages";
import { CAP } from "./types";
import { WorklistScreen } from "./screens/WorklistScreen";
import { EscaladeScreen } from "./screens/EscaladeScreen";
import { RecallWorklistScreen } from "./screens/RecallWorklistScreen";
import { BreakGlassReviewScreen } from "./screens/BreakGlassReviewScreen";

const validationModule: FeatureModule = {
  id: "validation",
  title: "Validation biologique",
  requiredCapabilities: [CAP.read],
  routes: [
    { path: "/validation", element: <WorklistScreen /> },
    { path: "/validation/escalades", element: <EscaladeScreen /> },
    // NOVA-028 — the biologiste lot-recall worklist (INV-4 → D-24).
    { path: "/validation/rappels", element: <RecallWorklistScreen /> },
    // NOVA-023 — the manager break-glass post-hoc review (VAL-3).
    { path: "/validation/breakglass-review", element: <BreakGlassReviewScreen /> },
  ],
  nav: [
    {
      id: "validation-worklist",
      path: "/validation",
      label: "nav.validation.worklist",
      icon: "✓",
      order: 40,
      section: "nav.section.production",
    },
    {
      id: "validation-escalade",
      path: "/validation/escalades",
      label: "nav.validation.escalade",
      icon: "◆",
      order: 41,
      section: "nav.section.production",
      requiredCapabilities: [CAP.acknowledge],
    },
    {
      id: "validation-recall",
      path: "/validation/rappels",
      label: "nav.validation.recall",
      icon: "⊘",
      order: 42,
      section: "nav.section.production",
      requiredCapabilities: [CAP.recallRead],
    },
    {
      id: "validation-breakglass-review",
      path: "/validation/breakglass-review",
      label: "nav.validation.bgreview",
      icon: "⚖",
      order: 43,
      section: "nav.section.production",
      requiredCapabilities: [CAP.review],
    },
  ],
  onRegister() {
    registerValidationMessages();
  },
};

export default validationModule;
